import React from 'react';
import { OrderDestination } from '@hero/schema';
import { cn } from '@hero/web-lib';
import { ArrowRightIcon } from '@hero/web-icons';
import { RegionLane, type RegionLaneEntry } from '../RegionName/RegionLane';

interface RouteDestination {
  displayName: string;
  customCode?: string;
  type?: string;
  _id?: unknown;
}

interface SimpleLocation {
  city?: { name: string; customCode?: string };
  fullAddress?: string;
}

interface LocationDetailsLike {
  city?: { name: string; customCode?: string };
  fullAddress?: string;
  _id?: string;
  name?: string;
}

export interface RouteDisplayProps {
  origin: SimpleLocation | OrderDestination | RouteDestination | LocationDetailsLike;
  destinations: Array<SimpleLocation | OrderDestination | RouteDestination | LocationDetailsLike>;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  /** Custom arrow element. Defaults to "→" that flips in RTL */
  arrow?: React.ReactNode;
}

const SIZE_CLASS: Record<string, string> = {
  small: 'text-xs',
  medium: 'text-sm',
  large: 'text-lg',
};

const isOrderDestination = (
  loc: SimpleLocation | OrderDestination | RouteDestination | LocationDetailsLike,
): loc is OrderDestination => 'address' in loc;

const isRouteDestination = (
  loc: SimpleLocation | OrderDestination | RouteDestination | LocationDetailsLike,
): loc is RouteDestination => 'displayName' in loc && !('address' in loc);

function toLaneEntry(
  loc: SimpleLocation | OrderDestination | RouteDestination | LocationDetailsLike | undefined,
): RegionLaneEntry | null {
  if (!loc) return null;
  if (isOrderDestination(loc)) {
    const city = loc.address?.city;
    if (!city?.customCode && !city?.name && !loc.address?.fullAddress) return null;
    return {
      customCode: city?.customCode,
      displayName: city?.name || loc.address?.fullAddress,
    };
  }
  if (isRouteDestination(loc)) {
    return { customCode: loc.customCode, displayName: loc.displayName };
  }
  const simple = loc as SimpleLocation | LocationDetailsLike;
  if (!simple.city?.name && !simple.fullAddress) return null;
  return {
    customCode: simple.city?.customCode,
    displayName: simple.city?.name || simple.fullAddress,
  };
}

/**
 * RouteArrowDisplay — renders "Origin → Destination" in the viewer's language.
 *
 * Resolves city names via `RegionLane` / `useTranslateRegions` when
 * `customCode` is available; falls back to snapshot `name` / `displayName`.
 */
export function RouteArrowDisplay({
  origin,
  destinations,
  size = 'medium',
  className,
  arrow,
}: RouteDisplayProps) {
  const stops = [origin, ...destinations]
    .map(toLaneEntry)
    .filter((entry): entry is RegionLaneEntry => entry !== null);

  const defaultArrow = (
    <ArrowRightIcon
      className="text-gray-400 flex-shrink-0 rtl:rotate-180"
      style={{ width: '1.2em', height: '1.2em' }}
      strokeWidth={2.5}
    />
  );

  if (stops.length === 0) {
    return <span className="text-muted-foreground">No route</span>;
  }

  return (
    <RegionLane
      stops={stops}
      separator={arrow ?? defaultArrow}
      className={cn('inline-flex items-center gap-1 whitespace-nowrap', SIZE_CLASS[size], className)}
      emptyFallback="No route"
    />
  );
}
